import React, { useEffect, useState, useCallback } from 'react';
import { CircleLoader } from '@hpstellar/core';

import useProductImpressions from '../../../hooks/useProductImpressionsV2';
import useCartSate, { useCartActions } from '../../../hooks/useCartState';
import useProductPrice from '../../../hooks/useProductPrice';
import { Helpers } from '../../../core/src/helpers';
import { formatGTMAction } from '../../../metrics/metrics-helpers';

const getPriceByCatEntryId = (prices, catEntryId) => {
    const priceData = prices && Object.values(prices).find(price => price.itemId === catEntryId);
    return priceData;
};

const EMPTY_OBJ = {};
const mapCarePackData = (
    cartId,
    componentKey,
    carepackMap,
    prices,
    carepackData,
    addCrossSell,
    addingCarePack,
    onAdd = () => {},
    orderItem = [],
) => {
    const { carepackHeader, carepackSubsHeader, cartCarePackOitems: missingHW = [] } = carepackData;
    const productList = [];
    const carePackProps = Object.keys(carepackMap).reduce((all, key) => {
        const carepackProducts = carepackMap[key];
        const isAdding = addingCarePack[key];
        //loop through carepacks and find first in stock product
        let inStockProduct;
        carepackProducts.forEach((product, index, position) => {
            if (inStockProduct) return;
            const { catEntryId, url, eligibleSku, xsellProdInfo } = product;
            const priceData = getPriceByCatEntryId(prices, catEntryId);
            const gtmPosition = orderItem.findIndex(i => i.partNumber === xsellProdInfo) + 1;
            if (priceData && !priceData.isOOS) {
                const {
                    subscriptionTerm,
                    prodName: description,
                    regularPrice: oldPrice,
                    salePrice: finalPrice,
                    salePriceSuffix,
                    sku,
                    msrp,
                } = priceData;
                const btnProps = addCrossSell
                    ? {
                          onClick: () => {
                              if (isAdding) return;
                              setTimeout(() => {
                                  onAdd(key, true);
                                  addCrossSell(priceData, 1, eligibleSku).then(() => onAdd(key, false));
                              }, 0);
                          },
                          children: isAdding ? <CircleLoader /> : 'Add',
                          ...formatGTMAction({
                              category: 'addToCart',
                              value: 'cart',
                              id: `${componentKey}-${sku}-${gtmPosition}`,
                              cartId,
                              quantity: 1,
                          }),
                      }
                    : null;

                inStockProduct = {
                    className: 'carepack-attachment',
                    title: subscriptionTerm ? carepackSubsHeader : carepackHeader,
                    description,
                    ViolatorProps: subscriptionTerm ? { label: `${subscriptionTerm} subscription` } : null,
                    LinkProps: url
                        ? {
                              children: 'Learn more',
                              to: url,
                              target: '_blank',
                              ...formatGTMAction({
                                  category: 'productClick',
                                  value: 'cart',
                                  id: `${componentKey}-${sku}-${gtmPosition}`,
                                  cartId,
                                  quantity: 1,
                              }),
                          }
                        : null,
                    product: { ...product, sku: priceData.sku, xsellProdInfo: key },
                    PriceBlockProps: {
                        oldPrice: finalPrice < oldPrice ? Helpers.formatCurrency(oldPrice, undefined, true) : null,
                        finalPrice: Helpers.formatCurrency(finalPrice, undefined, true),
                        subscriptionTermLabel: salePriceSuffix ? salePriceSuffix.trim() : null,
                        msrp,
                    },
                    ButtonProps: btnProps,
                    onChange: btnProps && btnProps.onClick,
                    isChecked: isAdding,
                };
                return;
            }
        });
        if (inStockProduct) {
            all[key] = inStockProduct;
            //track product for impressions
            productList.push(inStockProduct.product);
        }
        return all;
    }, {});

    //sort productList by position in orderItem
    productList.sort((a, b) => {
        const aIndex = orderItem.findIndex(item => item.partNumber === a.xsellProdInfo);
        const bIndex = orderItem.findIndex(item => item.partNumber === b.xsellProdInfo);
        return aIndex - bIndex;
    });

    return { carePackProps, productList, missingHW };
};

export default orderItem => {
    const { cartId, cartInfo } = useCartSate();
    const { cartView } = cartInfo || {};
    const { orderItem: orderItems = [] } = cartView || {};
    const { getCarePackInfo, addCrossSell } = useCartActions();
    const [carepackProducts, setCarepackProducts] = useState([]);
    const [carepackMap, setCarepackMap] = useState({});
    const [carepackData, setCarepackData] = useState({});
    const [addingCarePack, setAddingCarePack] = useState({});
    const { prices } = useProductPrice(carepackProducts);
    const orderLength = orderItem.length;
    const componentKey = 'cart-carepack';
    const { carePackProps, productList, missingHW } = mapCarePackData(
        cartId,
        componentKey,
        carepackMap,
        prices,
        carepackData,
        addCrossSell,
        addingCarePack,
        (sku, value) => {
            setAddingCarePack({ ...addingCarePack, [sku]: value });
        },
        orderItems,
    );
    useProductImpressions({
        products: productList && productList.length > 0 ? productList : [],
        list: 'cart',
        componentKey,
        trackMetrics: true,
        immediate: true,
        xsellMethod: componentKey,
    });
    const refreshCarePackInfo = useCallback(() => {
        return getCarePackInfo().then(data => {
            const { suggestionList = {}, carepackHeader, carepackSubsHeader, cartCarePackOitems } = data || {};
            const mapping = {};
            const carePackProducts = Object.keys(suggestionList).reduce((all, key) => {
                const carepackMap = suggestionList[key];
                //CTO skus contain a pipe delimiter
                const skuKey = key.split('|')[0];
                const carepackProducts = Object.values(carepackMap).map(item => {
                    const [catEntryId, url] = (item || '').split('|');
                    return { catEntryId, url, eligibleSku: key, xsellProdInfo: skuKey };
                });
                if (carepackProducts.length > 0) {
                    mapping[skuKey] = carepackProducts;
                }
                return all.concat(carepackProducts);
            }, []);
            if (carePackProducts.length > 0 || cartCarePackOitems) {
                setCarepackProducts(carePackProducts);
                setCarepackMap(mapping);
                setCarepackData({ carepackHeader, carepackSubsHeader, cartCarePackOitems });
            } else {
                setCarepackProducts([]);
                setCarepackMap(EMPTY_OBJ);
            }
        });
    });

    useEffect(() => {
        if (orderLength < 1) return;
        refreshCarePackInfo();
    }, [orderLength]);

    return { carePackProps, missingHW, refreshCarePackInfo };
};
